import React, { useState, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { Button, Alert, Form } from 'react-bootstrap';
import { PageLoader } from '../../Components/LoadingSpinner';
import { Plus, Pencil, Trash2, FileText, Building2, Download } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';
import { BreadcrumbComponent } from '../../Components/BreadCrumb';
import { useBuilding } from '../../Helpers/BuildingContext';
import { useOperatingCosts } from '../../hooks/operatingCosts/useOperatingCosts';
import { useOperatingCostCategories } from '../../contexts/OperatingCostCategoriesContext';
import { OperatingCost, formatInterval, calculateYearlyCost, CostInterval } from '../../types/operatingCosts';
import { getAttachmentDownloadUrl, formatFileSize, uploadOperatingCostAttachment } from '../../Helpers/OperatingCostAttachmentUtils';
import { formatCurrency } from '../../Helpers/FormatUtils';
import { getChartColor } from '../../Helpers/chartColors';
import OperatingCostModal from './OperatingCostModal';
import OperatingCostsSummary, { CategoryTotal } from './OperatingCostsSummary';
import './OperatingCostsPage.css';

const OperatingCostsPage: React.FC = () => {
    const { id: buildingKey } = useParams<{ id: string }>();
    const { currentBuilding, loadingBuilding } = useBuilding();
    const { categories } = useOperatingCostCategories();

    // Set document title
    const buildingName = currentBuilding?.summary?.name || 'Byggnad';
    useDocumentTitle(`Driftkostnader - ${buildingName}`);

    // Modal state
    const [showModal, setShowModal] = useState(false);
    const [editingCost, setEditingCost] = useState<OperatingCost | undefined>();
    const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);

    // Filter state
    const [filterCategory, setFilterCategory] = useState<string>('');

    // Use operating costs hook
    const {
        costs,
        loading,
        error,
        submitting,
        totalYearlyCost,
        addCost,
        updateCost,
        deleteCost,
        addAttachment,
        removeAttachment,
        clearError
    } = useOperatingCosts({ buildingKey });

    // Get category label by key
    const getCategoryLabel = (categoryKey: string): string => {
        const category = categories.find(c => c.key === categoryKey);
        return category?.label || 'Okänd kategori';
    };

    // Filter costs
    const filteredCosts = useMemo(() => {
        if (!filterCategory) return costs;
        return costs.filter(cost => cost.categoryKey === filterCategory);
    }, [costs, filterCategory]);

    // Group by category for display
    const costsByCategory = useMemo(() => {
        const grouped: { [key: string]: OperatingCost[] } = {};
        for (const cost of filteredCosts) {
            if (!grouped[cost.categoryKey]) {
                grouped[cost.categoryKey] = [];
            }
            grouped[cost.categoryKey].push(cost);
        }
        return grouped;
    }, [filteredCosts]);

    // Calculate filtered total
    const filteredYearlyCost = useMemo(() => {
        return filteredCosts.reduce((sum, cost) => {
            return sum + calculateYearlyCost(cost.amount, cost.interval);
        }, 0);
    }, [filteredCosts]);

    // Yearly total per category, largest first; colours follow the category list so they stay stable
    const categoryTotals = useMemo((): CategoryTotal[] => {
        return Object.entries(costsByCategory)
            .map(([key, categoryCosts]) => {
                const index = categories.findIndex(c => c.key === key);
                return {
                    key,
                    label: categories[index]?.label || 'Okänd kategori',
                    yearly: categoryCosts.reduce((sum, c) => sum + calculateYearlyCost(c.amount, c.interval), 0),
                    color: getChartColor(index >= 0 ? index : categories.length),
                };
            })
            .sort((a, b) => b.yearly - a.yearly);
    }, [costsByCategory, categories]);

    // Categories with costs (for filter dropdown)
    const categoriesWithCosts = useMemo(() => {
        const categoryKeys = new Set(costs.map(c => c.categoryKey));
        return categories.filter(c => categoryKeys.has(c.key));
    }, [costs, categories]);

    // Handle opening modal for add
    const handleAdd = () => {
        setEditingCost(undefined);
        setShowModal(true);
    };

    // Handle opening modal for edit
    const handleEdit = (cost: OperatingCost) => {
        setEditingCost(cost);
        setShowModal(true);
    };

    // Handle modal submit
    const handleSubmit = async (data: {
        categoryKey: string;
        amount: number;
        interval: CostInterval;
        description?: string;
        supplier?: string;
        filesToUpload?: File[];
        attachmentsToRemove?: string[];
    }) => {
        const { filesToUpload, attachmentsToRemove: toRemove, ...costData } = data;
        let costKey: string;

        if (editingCost) {
            await updateCost(editingCost.key, costData);
            costKey = editingCost.key;
        } else {
            const newCost = await addCost(costData);
            costKey = newCost.key;
        }

        // Handle file uploads
        if (filesToUpload && filesToUpload.length > 0 && buildingKey) {
            const user = getAuth().currentUser;
            if (user) {
                for (const file of filesToUpload) {
                    const result = await uploadOperatingCostAttachment(
                        buildingKey,
                        costKey,
                        file,
                        user.uid
                    );
                    if (result.success && result.attachment) {
                        await addAttachment(costKey, result.attachment);
                    }
                }
            }
        }

        // Handle attachment removals
        if (toRemove && toRemove.length > 0) {
            for (const attachmentKey of toRemove) {
                await removeAttachment(costKey, attachmentKey);
            }
        }

        setShowModal(false);
        setEditingCost(undefined);
    };

    // Handle delete
    const handleDelete = async (costKey: string) => {
        await deleteCost(costKey);
        setDeleteConfirm(null);
    };

    // Handle attachment download
    const handleDownloadAttachment = async (storagePath: string, fileName: string) => {
        try {
            const url = await getAttachmentDownloadUrl(storagePath);
            const link = document.createElement('a');
            link.href = url;
            link.download = fileName;
            link.target = '_blank';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (err) {
            console.error('Error downloading attachment:', err);
        }
    };

    // Loading state
    if (loading || loadingBuilding) {
        return <PageLoader />;
    }

    return (
        <>
            <BreadcrumbComponent
                items={[
                    { text: buildingName, link: `/building/${buildingKey}` },
                    { text: 'Driftkostnader' }
                ]}
            />

            <div className="operating-costs-page">
                {/* Header */}
                <div className="operating-costs-title-container">
                    <h1 className="page-title">Driftkostnader</h1>
                    <Button
                        variant="primary"
                        onClick={handleAdd}
                        className="operating-costs-add"
                    >
                        <Plus size={18} className="me-1" />
                        Lägg till kostnad
                    </Button>
                </div>

                {costs.length > 0 && (
                    <OperatingCostsSummary
                        yearlyTotal={filterCategory ? filteredYearlyCost : totalYearlyCost}
                        costCount={filteredCosts.length}
                        categoryTotals={categoryTotals}
                        filterLabel={filterCategory ? getCategoryLabel(filterCategory) : undefined}
                    />
                )}

                {/* Filter */}
                {categoriesWithCosts.length > 1 && (
                    <div className="operating-costs-filter mb-3">
                        <Form.Select
                            value={filterCategory}
                            onChange={(e) => setFilterCategory(e.target.value)}
                            style={{ maxWidth: '250px' }}
                        >
                            <option value="">Alla kategorier</option>
                            {categoriesWithCosts.map(cat => (
                                <option key={cat.key} value={cat.key}>
                                    {cat.label}
                                </option>
                            ))}
                        </Form.Select>
                    </div>
                )}

                {/* Error alert */}
                {error && (
                    <Alert variant="danger" dismissible onClose={clearError}>
                        {error}
                    </Alert>
                )}

                {/* Costs list */}
                <div className="operating-costs-list">
                    {filteredCosts.length === 0 ? (
                        <div className="std-box operating-costs-empty">
                            <p className="text-muted mb-3">
                                Inga driftkostnader registrerade för denna byggnad
                            </p>
                            <Button variant="outline-primary" onClick={handleAdd}>
                                <Plus size={16} className="me-1" />
                                Lägg till första kostnaden
                            </Button>
                        </div>
                    ) : (
                        Object.entries(costsByCategory).map(([categoryKey, categoryCosts]) => (
                            <div key={categoryKey} className="cost-category-group">
                                <h5 className="category-header">
                                    {getCategoryLabel(categoryKey)}
                                    <span className="category-header-total">
                                        {formatCurrency(categoryTotals.find(t => t.key === categoryKey)?.yearly ?? 0)}/år
                                    </span>
                                </h5>

                                {categoryCosts.map(cost => (
                                    <div key={cost.key} className="std-box cost-card">
                                            {deleteConfirm === cost.key ? (
                                                <div className="delete-confirm">
                                                    <span className="text-danger">
                                                        Ta bort denna driftkostnad?
                                                    </span>
                                                    <div className="delete-confirm-actions">
                                                        <Button
                                                            variant="danger"
                                                            size="sm"
                                                            onClick={() => handleDelete(cost.key)}
                                                        >
                                                            Ta bort
                                                        </Button>
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => setDeleteConfirm(null)}
                                                        >
                                                            Avbryt
                                                        </Button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <>
                                                    <div className="cost-card-header">
                                                        <div className="cost-amount">
                                                            <strong>{formatCurrency(cost.amount)}</strong>
                                                            <span className="cost-interval">
                                                                {formatInterval(cost.interval)}
                                                            </span>
                                                        </div>
                                                        <div className="cost-yearly">
                                                            {formatCurrency(calculateYearlyCost(cost.amount, cost.interval))}/år
                                                        </div>
                                                    </div>

                                                    {cost.supplier && (
                                                        <div className="cost-supplier">
                                                            <Building2 size={14} className="me-1" />
                                                            {cost.supplier}
                                                        </div>
                                                    )}

                                                    {cost.description && (
                                                        <p className="cost-description text-muted">
                                                            {cost.description}
                                                        </p>
                                                    )}

                                                    {/* Attachments */}
                                                    {cost.attachments && Object.keys(cost.attachments).length > 0 && (
                                                        <div className="cost-attachments">
                                                            {Object.values(cost.attachments).map(attachment => (
                                                                <button
                                                                    key={attachment.key}
                                                                    className="attachment-item"
                                                                    onClick={() => handleDownloadAttachment(
                                                                        attachment.storagePath,
                                                                        attachment.fileName
                                                                    )}
                                                                    title={`Ladda ner ${attachment.fileName}`}
                                                                >
                                                                    <FileText size={14} />
                                                                    <span className="attachment-name">
                                                                        {attachment.fileName}
                                                                    </span>
                                                                    <span className="attachment-size">
                                                                        ({formatFileSize(attachment.fileSize)})
                                                                    </span>
                                                                    <Download size={12} />
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}

                                                    <div className="cost-actions">
                                                        <Button
                                                            variant="outline-secondary"
                                                            size="sm"
                                                            onClick={() => handleEdit(cost)}
                                                            title="Redigera"
                                                        >
                                                            <Pencil size={14} />
                                                        </Button>
                                                        <Button
                                                            variant="outline-danger"
                                                            size="sm"
                                                            onClick={() => setDeleteConfirm(cost.key)}
                                                            title="Ta bort"
                                                        >
                                                            <Trash2 size={14} />
                                                        </Button>
                                                    </div>
                                                </>
                                            )}
                                    </div>
                                ))}
                            </div>
                        ))
                    )}
                </div>
            </div>

            {/* Add/Edit Modal */}
            {showModal && (
                <OperatingCostModal
                    show={showModal}
                    onHide={() => {
                        setShowModal(false);
                        setEditingCost(undefined);
                    }}
                    onSubmit={handleSubmit}
                    cost={editingCost}
                    submitting={submitting}
                />
            )}
        </>
    );
};

export default OperatingCostsPage;
